import React, { useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import moment from 'moment';
import { DEFAULT_PLACEMENT_OVERVIEW_TAB } from 'src/components/pages/placementsOverview/placementOverviewTabs';
import PlacementExportMenu from 'src/components/shared/placementExportMenu';
import PlacementUpdateMenu from 'src/components/shared/placementUpdateMenu';
import { TRANSFER_PENDING } from 'src/config/AccommodationStatusNames';
import * as LDAP from 'src/config/LDAP';
import { RTW_SYSTEM } from 'src/constants';
import { getAccommodationQueryOptions } from 'src/services/accommodation/accommodationQueries';
import { setJmrFlowType } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { setIsEmailModalOpen } from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { constructIncidentLink, constructRosieCaseLink } from 'src/utils/constructRTWLinks';
import { getTzShort } from 'src/utils/dateUtils';
import { stripOutLeadingHash } from 'src/utils/helpers';
import { CaseSources, JmrFlowTypes } from 'src/utils/jmrUtils';
import { placementDetailsToPlacementInfo } from 'src/utils/placementDetailUtils';

/**
 * Default component
 */
interface TopOptionsProps {
  setIsDeleteModalOpen: (isOpen: boolean) => void;
}
const TopOptions = ({ setIsDeleteModalOpen }: TopOptionsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { placementId } = useParams();
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const updateButtonRef = useRef<HTMLButtonElement>(null);
  const { ldap } = useAppSelector((state) => state.user);
  const { data: placementDetails, isSuccess: isPlacementsSuccess } = useQuery(
    getAccommodationQueryOptions.accommodationDetails(+placementId!),
  );
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isUpdateMenuOpen, setIsUpdateMenuOpen] = useState(false);
  const isAdmin = ldap.includes(LDAP.ADMIN);

  return isPlacementsSuccess ? (
    <Row maxWidth="100%" alignmentVertical="top" widths={['fit', 'fill']}>
      <Column spacing="100">
        <Row>
          {/* Associate/Placement Info */}
          <Text type="h400" data-cy="placement-details__caseInformation-header">
            Case ID: {placementDetails.accommodationType} |{' '}
            {!isNil(placementDetails.externalCaseId) && placementDetails.accommodationType === CaseSources.Austin ? (
              <Tooltip title="Open case in AUSTIN" position="bottom">
                <Link
                  href={constructIncidentLink(stripOutLeadingHash(placementDetails.externalCaseId))}
                  target="_blank"
                  rel="noopener noreferrer"
                  type="secondary"
                >
                  {placementDetails.externalCaseId}
                </Link>
              </Tooltip>
            ) : (
              placementDetails.externalCaseId
            )}
          </Text>
          <Tag type="warning" data-cy="placement-details__status-tag">
            {placementDetails?.accommodationStatus?.status}
          </Tag>
        </Row>
        <Row spacing="200">
          {placementDetails.site?.participatingInDecisionHubPilot &&
            placementDetails.accommodationType === CaseSources.Austin &&
            placementDetails.decisionHubLink && (
              <>
                <Tooltip position="bottom" title="Access limited to L6+ decision makers">
                  <Text type="b300">
                    <Link
                      type="secondary"
                      href={placementDetails.decisionHubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Decision Hub
                    </Link>
                  </Text>
                </Tooltip>
                <Text type="b300" color="secondary">
                  •
                </Text>
              </>
            )}
          {placementDetails.accommodationType === CaseSources.Austin && (
            <>
              <Text type="b300">
                <Link type="secondary" href={constructRosieCaseLink(placementDetails.externalCaseId)}>
                  Rosie
                </Link>
              </Text>
              <Text type="b300" color="secondary">
                •
              </Text>
            </>
          )}
          <Text type="b300" color="secondary">
            Last updated: {moment(placementDetails.updatedAt).format('L [at] h:mma')} {getTzShort()} by{' '}
            {placementDetails.editedBy === RTW_SYSTEM ? (
              placementDetails.editedBy
            ) : (
              <Link
                href={`https://phonetool.amazon.com/users/${placementDetails.editedBy}`}
                target="_blank"
                rel="noopener noreferrer"
                type="secondary"
              >
                {placementDetails.editedBy}@
              </Link>
            )}
          </Text>
        </Row>
      </Column>
      <Row spacing="300" alignmentHorizontal="end">
        {/* Delete Placement */}
        {isAdmin && (
          <Button data-cy="placement-details__delete-btn" type="link" onClick={() => setIsDeleteModalOpen(true)}>
            Delete
          </Button>
        )}

        {/* Send Email/Update/Export */}
        {placementDetails.accommodationStatus?.status !== TRANSFER_PENDING && (
          <>
            <Button
              type="secondary"
              onClick={() => dispatch(setIsEmailModalOpen(true))}
              data-cy="placement-detail-send-email__btn"
            >
              Send Email
            </Button>
            <Button type="primary" ref={exportButtonRef} onClick={() => setIsExportMenuOpen(true)}>
              Export
              <Icon tokens={chevronDownSmallTokens} />
            </Button>
            <Button
              id="update-placement-open-menu-btn"
              data-cy="placement-detail__update-btn"
              data-testid="placement-detail__update-btn"
              type="primary"
              ref={updateButtonRef}
              onClick={() => setIsUpdateMenuOpen(true)}
            >
              Update
              <Icon tokens={chevronDownSmallTokens} />
            </Button>
          </>
        )}

        {/* Accept Transfer */}
        {placementDetails.accommodationStatus?.status === TRANSFER_PENDING && (
          <Button
            type="primary"
            data-cy="placement-details__accept-transfer-btn"
            onClick={() => {
              dispatch(setJmrFlowType(JmrFlowTypes.CompleteTransfer));
              navigate(`/newJMR/${placementId}/update/${JmrFlowTypes.CompleteTransfer}`, {
                state: { returnTo: location.pathname },
              });
            }}
          >
            Accept Transfer
          </Button>
        )}

        <PlacementUpdateMenu
          isMenuOpen={isUpdateMenuOpen}
          anchorRef={updateButtonRef}
          showAdminEdit={isAdmin}
          placement={placementDetailsToPlacementInfo(placementId!, placementDetails)}
          actions={{
            closeMenu: () => setIsUpdateMenuOpen(false),
            onStatusUpdateSuccess: () =>
              queryClient.invalidateQueries({
                queryKey: getAccommodationQueryOptions.accommodationDetails(+placementId!).queryKey,
              }),
            onTransferSuccess: () =>
              navigate(`/placementsOverview/${placementDetails.fc}/${DEFAULT_PLACEMENT_OVERVIEW_TAB}`),
          }}
        />

        <PlacementExportMenu
          isMenuOpen={isExportMenuOpen}
          closeMenu={() => setIsExportMenuOpen(false)}
          placementId={placementId!}
          anchorRef={exportButtonRef}
          newestJmrFileName={placementDetails.newestJmrFileName}
          countryId={placementDetails.countryId}
          allocatedJobShifts={placementDetails.allocatedJobShifts}
          jobModificationsEnabled={placementDetails.site?.jobModificationsEnabled}
        />
      </Row>
    </Row>
  ) : null;
};

export default TopOptions;
